import type { INavEntry } from './INavEntry';
import type { INavGroup } from './INavGroup';
import type { INavMode } from './INavMode';
import type { INavModuleMeta } from './INavModuleMeta';
import type { INavOnboardGroup } from './INavOnboardGroup';
import type { INavPlacement } from './INavPlacement';
import type { INavShelf } from './INavShelf';
import type { INavSystemItem } from './INavSystemItem';
import type { INavWorkspaceType } from './INavWorkspaceType';
import { customModuleCategory, customModulesIn } from './customModules';
import { navigationModel as model } from './navigationModel';

const groupsByKey = new Map(model.groups.map((g) => [g.key, g]));
const FALLBACK_MODULE: INavModuleMeta = { icon: 'layout-grid', tone: 'core', group: '', listed: true, types: 1 };

/** Власний модуль простору (ADR-025; modIco прототипу): плитка layout-grid тону своєї категорії, група — група категорії, один тип запису. */
function customModuleNav(key: string): INavModuleMeta | undefined {
  const category = customModuleCategory(key);
  if (category === undefined) return undefined;
  const group = model.groups.find((g) => g.category === category);
  return { icon: 'layout-grid', tone: group?.tone ?? 'core', group: group?.key ?? '', listed: true, types: 1 };
}

/** Усі режими навігації в порядку меню «Режим навігації». */
export const navModes = (): INavMode[] => model.modes;

/** Режим за ключем; невідомий або порожній ключ — типовий режим (Депутат). */
export function navMode(key: string | null | undefined): INavMode {
  return model.modes.find((m) => m.key === key) ?? model.modes.find((m) => m.key === model.defaultMode) ?? model.modes[0]!;
}

/** Група верхнього меню за ключем пакета групи. */
export const navGroup = (key: string): INavGroup | undefined => groupsByKey.get(key);

/** Група за категорією каталогу (Management → management): посилання «Усі модулі?cat=<категорія>» зі шапок сторінок модулів. */
export const groupByCategory = (category: string | null | undefined): INavGroup | undefined => model.groups.find((g) => g.category === category);

/** Групи режиму в порядку верхнього меню (перша — «Головна»). */
export const modeGroups = (mode: INavMode): INavGroup[] => mode.groups.map(navGroup).filter((g): g is INavGroup => !!g);

/** Іконка, тон, група й видимість модуля (і власного модуля простору); невідомий модуль — плитка layout-grid тону core (як modIco прототипу). */
export const moduleNav = (key: string): INavModuleMeta => model.modules[key] ?? customModuleNav(key) ?? FALLBACK_MODULE;

/** Плитка типу запису (рядки «Встановлених модулів» і «Форм»): власна іконка дочірньої таблиці прототипу, інакше — плитка модуля. */
export const typeNav = (moduleKey: string, rtKey: string): INavModuleMeta => ({ ...moduleNav(moduleKey), ...model.typeIcons[rtKey] });

/** Системний пункт меню групи (home, search, tags, customization, administration, tagManagement). */
export const systemItem = (key: string): INavSystemItem | undefined => model.systemItems[key];

/** Системні модулі сторінки «Усі модулі» (Головна, Адміністрування, Налаштування) — ключі systemItems. */
export const systemModules = (): string[] => model.systemModules;

/** Типові закріплені модулі (прототип v4: основні модулі в порядку модулів простору), доки користувач не закріпив власні. */
export const defaultPins = (): string[] => model.defaultPins;

/** Полиці лівої колонки «Усі модулі» («Робота», «Парламент»…) з ключами груп; 'system' — розділ системних модулів. */
export const shelves = (): INavShelf[] => model.shelves;

/** Групи верхнього меню в порядку моделі (сторінка «Усі модулі» показує їх розділами). */
export const navGroups = (): INavGroup[] => model.groups;

/** Назва групи як категорії каталогу («Основа», «Комітети», «Законотворення») — розділи й ліва колонка «Усіх модулів». */
export const catalogLabel = (group: INavGroup) => (group.home ? group.label : group.title);

/** Необов'язкові модулі екрана «Створити робочий простір» за категоріями в порядку прототипу (CAT_ORDER, TEMPLATES). */
export const onboardingGroups = (): INavOnboardGroup[] => model.onboarding;

/** Тип робочого простору (підпис, множина, колір); невідомий тип — офіс депутата. */
export const workspaceType = (type: string): INavWorkspaceType => model.workspaceTypes[type] ?? model.workspaceTypes['mp']!;

/** Порядок типів робочих просторів у перемикачі (як WS_TYPES прототипу). */
export const workspaceTypeOrder = (): string[] => Object.keys(model.workspaceTypes);

/**
 * Модулі групи в порядку меню простору (прототип v4: за ws.modules простору): у парламенті — modules, в інших просторах — otherModules,
 * якщо група його має; наприкінці — власні модулі просторів цієї категорії (ADR-025: newModule додає модуль у кінець ws.modules).
 */
export const groupModules = (group: INavGroup, parliament = true): string[] =>
  [...(parliament ? group.modules : group.otherModules ?? group.modules), ...customModulesIn(group.category)];

/** Пункти випадного меню групи: системні сторінки, модулі (та ярлики «модуль/тип») у порядку простору, підгрупи із заголовками. */
export function groupEntries(group: INavGroup, parliament = true): INavEntry[] {
  const module = (entry: string): INavEntry => {
    const [key, recordType] = entry.split('/');
    return recordType ? { kind: 'module', key: key!, recordType } : { kind: 'module', key: key! };
  };
  return [
    ...group.before.map((key): INavEntry => ({ kind: 'system', key })),
    ...groupModules(group, parliament).map(module),
    ...group.subgroups.flatMap((s) => [{ kind: 'heading', key: s.key, label: s.label } as INavEntry, ...s.modules.map(module)]),
    ...group.after.map((key): INavEntry => ({ kind: 'system', key })),
  ];
}

/**
 * Місце модуля для крихт (прототип v4 grpCrumb: категорія модуля через CRUMB_CAT): група категорії модуля («Експерти › Мережа експертів»),
 * а для групи з батьківською (crumbParent: Development) — батьківська група й підгрупа (крихта «Управління › Розвиток» › «Навчання»).
 */
export function placement(moduleKey: string): INavPlacement | null {
  const own = navGroup(moduleNav(moduleKey).group);
  const parent = own?.crumbParent ? navGroup(own.crumbParent) : undefined;
  const subgroup = parent?.subgroups.find((s) => s.key === own?.key);
  if (parent && subgroup) return { group: parent, subgroup };
  return own ? { group: own, subgroup: null } : null;
}

/**
 * Активна пігулка верхнього меню для відкритого модуля в режимі: власна група модуля, інакше група, що містить його підгрупу,
 * інакше група, у меню якої є сам модуль (task у «Головній»); ярлик дочірньої таблиці «модуль/тип» (cmp/vol у «Виборцях») пігулку не
 * робить активною — прототип v4 порівнює модуль-батько таблиці (ek = parent || key) з модулями групи; не в режимі — null.
 */
export function activeGroupKey(mode: INavMode, moduleKey: string, _recordType?: string): string | null {
  const groups = modeGroups(mode);
  const own = moduleNav(moduleKey).group;
  if (groups.some((g) => g.key === own)) return own;
  const parent = groups.find((g) => g.subgroups.some((s) => s.modules.includes(moduleKey)));
  if (parent) return parent.key;
  return groups.find((g) => g.modules.includes(moduleKey))?.key ?? null;
}
